import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContentBlock } from '@/types/newsService';
import { cn } from '@/lib/cn';

import { CalloutBlock } from './CalloutBlock';
import { CodeBlock } from './CodeBlock';
import { DividerBlock } from './DividerBlock';
import { EmbedBlock } from './EmbedBlock';
import { HeadingBlock } from './HeadingBlock';
import { ImageBlock } from './ImageBlock';
import { ListBlock } from './ListBlock';
import { ParagraphBlock } from './ParagraphBlock';
import { QuoteBlock } from './QuoteBlock';

export interface ToolbarProps {
  hasSections: boolean;
  showCollapseAll: boolean;
  toggleAll: () => void;
}

interface Props {
  blocks: ContentBlock[];
  renderToolbar?: (props: ToolbarProps) => ReactNode;
}

interface Section {
  heading: ContentBlock | null;
  blocks: ContentBlock[];
  key: string;
}

function groupBlocksIntoSections(blocks: ContentBlock[]): Section[] {
  const sections: Section[] = [];
  let current: Section = { heading: null, blocks: [], key: 'intro' };

  for (const block of blocks) {
    if (block.type === 'heading') {
      if (current.heading || current.blocks.length > 0) {
        sections.push(current);
      }
      current = { heading: block, blocks: [], key: block._key };
    } else {
      current.blocks.push(block);
    }
  }
  if (current.heading || current.blocks.length > 0) {
    sections.push(current);
  }

  return sections;
}

function renderBlock(block: ContentBlock): ReactNode {
  switch (block.type) {
    case 'paragraph':
      return <ParagraphBlock key={block._key} block={block} />;
    case 'image':
      return <ImageBlock key={block._key} block={block} />;
    case 'quote':
      return <QuoteBlock key={block._key} block={block} />;
    case 'list':
      return <ListBlock key={block._key} block={block} />;
    case 'code':
      return <CodeBlock key={block._key} block={block} />;
    case 'embed':
      return <EmbedBlock key={block._key} block={block} />;
    case 'divider':
      return <DividerBlock key={block._key} />;
    case 'callout':
      return <CalloutBlock key={block._key} block={block} />;
    default:
      return null;
  }
}

interface CollapsibleSectionProps {
  section: Section;
  forceExpanded: boolean;
  collapseCounter: number;
  onExpandChange: (key: string, expanded: boolean) => void;
}

const CollapsibleSection = memo(function CollapsibleSection({
  section,
  forceExpanded,
  collapseCounter,
  onExpandChange,
}: CollapsibleSectionProps) {
  const [localExpanded, setLocalExpanded] = useState(false);

  useEffect(() => {
    if (collapseCounter > 0) {
      setLocalExpanded(false);
    }
  }, [collapseCounter]);

  const expanded = forceExpanded || localExpanded;

  const toggle = useCallback(() => {
    const next = !localExpanded;
    setLocalExpanded(next);
    if (section.heading) {
      onExpandChange(section.key, next);
    }
  }, [localExpanded, section.heading, section.key, onExpandChange]);

  // Intro section (no heading) — always visible
  if (!section.heading) {
    return <div>{section.blocks.map(renderBlock)}</div>;
  }

  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between text-left transition-opacity active:opacity-80"
      >
        <div className="flex-1">
          <HeadingBlock block={section.heading} />
        </div>
        <span
          className={cn(
            'pl-2 pt-5 text-xs',
            expanded ? 'text-fg-muted' : 'text-brand',
          )}
        >
          {expanded ? '▲' : '▼'}
        </span>
      </button>
      {expanded && <div>{section.blocks.map(renderBlock)}</div>}
    </div>
  );
});

/**
 * Portable-text content renderer with collapsible sections (one section per
 * `heading` block). The intro section (everything before the first heading)
 * is always visible. Optional `renderToolbar` lets the caller (e.g.
 * ArticleDetail) override the expand/collapse-all button placement.
 *
 * Ported from mobile-app/src/components/news/blocks/BlockRenderer.tsx.
 */
export const BlockRenderer = memo(function BlockRenderer({ blocks, renderToolbar }: Props) {
  const { t } = useTranslation('news');
  const sections = useMemo(() => groupBlocksIntoSections(blocks), [blocks]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [collapseCounter, setCollapseCounter] = useState(0);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const headingSections = useMemo(
    () => sections.filter((s) => s.heading !== null),
    [sections],
  );
  const hasSections = headingSections.length > 0;

  // Check if all sections are individually expanded
  const allManuallyExpanded =
    hasSections && headingSections.every((s) => expandedKeys.has(s.key));
  const showCollapseAll = allExpanded || allManuallyExpanded;

  const handleSectionExpandChange = useCallback((key: string, expanded: boolean) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (showCollapseAll) {
      setAllExpanded(false);
      setCollapseCounter((c) => c + 1);
      setExpandedKeys(new Set());
    } else {
      setAllExpanded(true);
    }
  }, [showCollapseAll]);

  const introSection = sections.find((s) => s.heading === null) || null;
  const headingOnlySections = sections.filter((s) => s.heading !== null);

  const toolbar = renderToolbar
    ? renderToolbar({ hasSections, showCollapseAll, toggleAll })
    : hasSections && (
        <button
          type="button"
          onClick={toggleAll}
          className="mb-1 ml-auto flex items-center gap-1.5 px-1 py-2 transition-opacity active:opacity-80"
        >
          <span className="text-[13px] font-semibold text-brand">
            {showCollapseAll ? t('collapseAll') : t('expandAll')}
          </span>
          <span
            className={cn(
              'text-[10px]',
              showCollapseAll ? 'text-fg-muted' : 'text-brand',
            )}
          >
            {showCollapseAll ? '▲' : '▼'}
          </span>
        </button>
      );

  return (
    <div className="px-5">
      {introSection && (
        <CollapsibleSection
          key={introSection.key}
          section={introSection}
          forceExpanded={allExpanded}
          collapseCounter={collapseCounter}
          onExpandChange={handleSectionExpandChange}
        />
      )}

      {toolbar}

      {headingOnlySections.map((section) => (
        <CollapsibleSection
          key={section.key}
          section={section}
          forceExpanded={allExpanded}
          collapseCounter={collapseCounter}
          onExpandChange={handleSectionExpandChange}
        />
      ))}
    </div>
  );
});
